import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


import { Store } from '@ngrx/store';
import { BaseSite, BaseSiteService, LanguageService } from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import { combineLatest, Observable, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';

import { DpCmsFlexPropsMapperService } from '@app/core/cms-flex/services';
import { CmsFlexComponent } from '@app/core/cms-flex/types';

import { HOME_PAGE_CATEGORY, PRODUCT_SUBCATEGORIES_ITEM_COUNT } from '../../constants';
import { DpProductSubcategoriesSwitcherModel } from '../../models';
import { ProductSubcategoriesActions } from '../../store';
import { categoriesSelector, categoriesSwitcherSelector } from '../../store/product-subcategories.selectors';
import { ProductSubcategories } from '../../types';

@Component({
    selector: 'dp-home-product-subcategories',
    templateUrl: './home-product-subcategories.component.html',
    styleUrl: './home-product-subcategories.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DpHomeProductSubcategoriesComponent implements OnInit {
  @HostBinding('class') classes = '';

  readonly categories$: Observable<ProductSubcategories | undefined>;
  readonly switcher$: Observable<DpProductSubcategoriesSwitcherModel>;
  protected readonly flexComponentMapper = inject(DpCmsFlexPropsMapperService);

  protected readonly component = inject(CmsComponentData<CmsFlexComponent>);
  protected readonly baseSiteService = inject(BaseSiteService);
  protected readonly languageService = inject(LanguageService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly store = inject(Store);

  constructor() {
    this.categories$ = this.store.select(categoriesSelector);
    this.switcher$ = this.store.select(categoriesSwitcherSelector);
  }

  ngOnInit(): void {
    this.initLoadData();
    this.initVisibleItems();
    this.component.data$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.classes = this.flexComponentMapper.mapSlotBackgroundColor(data.slotBackgroundColor);
    });
  }

  setActive(activeIndex: number): void {
    this.store.dispatch(ProductSubcategoriesActions.setActiveSubcategories({ activeIndex }));
  }

  protected initLoadData(): void {
    combineLatest([this.baseSiteService.getActive().pipe(distinctUntilChanged()), this.languageService.getActive()])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([activeId]) => this.baseSiteService.get(activeId).pipe(take(1))),
        filter(Boolean)
      )
      .subscribe((activeSite: BaseSite) => {
        const productCatalog = activeSite?.productCatalog?.id || '';
        this.loadData(productCatalog);
      });
  }

  protected loadData(productCatalog: string): void {
    this.store.dispatch(
      ProductSubcategoriesActions.loadProductSubcategories({
        productCatalog,
        categoryCode: HOME_PAGE_CATEGORY
      })
    );
  }

  protected initVisibleItems(): void {
    this.store.dispatch(
      ProductSubcategoriesActions.setVisibleItemsCount({ visibleItemsCount: PRODUCT_SUBCATEGORIES_ITEM_COUNT.DESKTOP })
    );
  }
}
